<template>
  <div id="voicemail_drop_list">
    <div
      v-if="isLoading"
      id="voicemail_drop_list_loading"
      class="overflow-hidden"
      style="height: 250px"
    >
      <SkeletonLoader :page="loaderPages.NAV_LIST" />
    </div>

    <div v-else class="flex flex-col">
      <div class="flex flex-col">
        <div class="p-2 flex flex-col gap-2">
          <span class="secondary_color text-[13px]">
            Please drop the voicemail after the beep
          </span>
          <CustomInput
            format="text"   
            v-model="searchQuery"
            placeholder="Search voicemails"
            type="primary"
            :auto-focus="true"
            :icon="imageNames.SEARCH_VARIANT1"
            size="size-4"
            alt="search_icon"
            @input="handleSearch"
          />
        </div>

        <div class="flex flex-col px-2 pb-2 h-[210px] overflow-y-scroll">
          <template v-if="voicemails.length > 0">
            <div
              v-for="voicemail in voicemails"
              :key="voicemail.id"
              :class="[
                'dropdown_item flex-shrink-0 flex items-center flex-row px-3 py-2 rounded-lg',
                {
                  dropdown_item_selected:
                    selectedVoicemail?.id === voicemail.id,
                },
              ]"
              @click="handleVoicemailSelect(voicemail)"
            >
              <div class="flex items-center gap-2">
                <button @click.stop="togglePlay(voicemail)">
                  <ThemeImage
                    :icon="
                      playingVoicemailId === voicemail.id
                        ? imageNames.CIRCLE_PAUSE_ICON
                        : imageNames.CIRCLE_PLAY_ICON
                    "
                    alt="play-pause"
                    class="size-5"
                  />
                </button>
                <span class="text-sm">{{ voicemail.name }}</span>
              </div>
            </div>
          </template>

          <EmptyScreen
            v-else
            source="voice_mails"
            text="No voicemails found!"
          />
        </div>
      </div>

      <div class="flex flex-row justify-end p-4 border_top">
        <a-button
          class="primary_medium w-fit"
          :disabled="!selectedVoicemail || isDropping"
          :loading="isDropping"
          @click="handleDropVoicemail"
        >
          {{ isDropping ? "Dropping..." : "Drop Voicemail" }}
        </a-button>
      </div>
    </div>

    <audio ref="audioPlayer" @ended="handleAudioEnded" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch} from "vue";
import { message } from "ant-design-vue";
import { debounce } from "lodash";
import { imageNames } from "@/src/constants/imageConstants.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { dialerTypes } from "@/src/constants/callConstants.js";
import CallService from "@/src/api/CallService.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import { getCurrentConnection} from "@/src/utils/callsUtils.js";
import store from "@/src/store/index.js";

const emit = defineEmits(["success"]);

const campaignCallSid = computed(() => store.getters["callsCampaign/campaignCallSid"]);
const isParallelCampaign = computed(() => store.getters["callsCampaign/campaignType"] === dialerTypes.PARALLEL_DIALER);

const searchQuery = ref("");
const voicemails = ref([]);
const selectedVoicemail = ref(null);
const playingVoicemailId = ref(null);
const isLoading = ref(false);
const isDropping = ref(false);
const audioPlayer = ref(null);

const fetchVoicemails = async (search = "") => {
  try {
    isLoading.value = true;
    const params = {
      page: 1,
      page_size: 20,
    };

    if (search) {
      params.search = search;
    }
    const response = await CallService.fetchVoicemails(params);

    if (!response?.success) {
      throw new Error(response?.error || "Failed to fetch voicemails");
    }
    voicemails.value = response.voicemails || [];
  } catch (error) {
    console.error("Error fetching voicemails:", error);
    message.error("Failed to load voicemails. Please try again.");
  } finally {
    isLoading.value = false;
  }
};

const debouncedSearch = debounce((search) => {
  fetchVoicemails(search);
}, 300);

const handleSearch = () => {
  if (!searchQuery.value) {
    debouncedSearch.cancel();
    fetchVoicemails();
    return;
  }

  debouncedSearch(searchQuery.value);
};

const handleVoicemailSelect = (voicemail) => {
  if (!voicemail) return;
  selectedVoicemail.value = voicemail;
};

const togglePlay = async (voicemail) => {
  if (!voicemail?.source) {
    message.error("Voicemail source not available");
    return;
  }

  try {
    if (playingVoicemailId.value === voicemail.id) {
      audioPlayer.value.pause();
      playingVoicemailId.value = null;
    } else {
      if (audioPlayer.value) {
        audioPlayer.value.src = voicemail.source;
        await audioPlayer.value.play();
        playingVoicemailId.value = voicemail.id;
      }
    }
  } catch (error) {
    console.error("Error playing voicemail:", error);
    message.error("Failed to play voicemail");
    playingVoicemailId.value = null;
  }
};

const handleAudioEnded = () => {
  playingVoicemailId.value = null;
};

const getCallSidForVoicemailDrop = () => {
  try {
    const currentConnection = getCurrentConnection();
    if (!currentConnection?.parameters?.CallSid) {
      return null;
    }
    const connectionCallSid = currentConnection?.parameters?.CallSid;
    const localCampaignCallSid = campaignCallSid?.value ?? null;
    
    const callSid = isParallelCampaign.value 
      ? (localCampaignCallSid || connectionCallSid)
      : connectionCallSid;
    
    return callSid;
  } catch (error) {
    console.error("Error getting call SID:", error);
    return null;
  }
};

const handleDropVoicemail = async () => {
  if (!selectedVoicemail.value) {
    message.error("Please select a voicemail");
    return;
  }

  try {
    isDropping.value = true; 
    const callSid = getCallSidForVoicemailDrop();

    if (!callSid) {
      throw new Error("No active call found");
    }

    const response = await CallService.dropVoicemail(
      callSid,
      selectedVoicemail.value.id
    );

    if (response?.success) {
      emit("success");
    } else {
      throw new Error(response?.error || "Failed to drop voicemail");
    }
  } catch (error) {
    console.error("Error dropping voicemail:", error);
    message.error("Failed to drop voicemail. Please try again.");
  } finally {
    isDropping.value = false;
  }
};

const refreshSelection = (newVoicemails) => {
  try {
    if (!selectedVoicemail.value) return;
    
    const isSelectedVoicemailInList = newVoicemails.some(
      (voicemail) => voicemail.id === selectedVoicemail.value.id
    );
    
    if (!isSelectedVoicemailInList) {
      const selectedId = selectedVoicemail.value.id;
      selectedVoicemail.value = null;
      
      if (playingVoicemailId.value === selectedId) {
        if (audioPlayer.value) {
          audioPlayer.value.pause();
        }
        playingVoicemailId.value = null;
      }
    }
  } catch (error) {
    console.error("Error refreshing selection:", error);
  }
};

watch(voicemails, refreshSelection);

onMounted(() => {
  fetchVoicemails();
});

onUnmounted(() => {
  if (audioPlayer.value) {
    audioPlayer.value.pause();
  }
  debouncedSearch.cancel();
});
</script>

<style scoped>
.dropdown_item_selected {
  background-color: var(--white-opacity-10);
}
</style>
